
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, Shield } from 'lucide-react';
import { useCreatePaymentSession } from '@/hooks/usePayments';
import type { Payment } from '@/types/payments';

interface PaymentButtonProps {
  payment: Payment;
  disabled?: boolean;
}

const PaymentButton: React.FC<PaymentButtonProps> = ({ payment, disabled = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const createSessionMutation = useCreatePaymentSession();

  const handlePayment = async () => {
    setIsLoading(true);
    
    try {
      const { url } = await createSessionMutation.mutateAsync({
        payment_id: payment.id,
        success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/reservations`,
      });
      
      // Rediriger vers Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Erreur lors de la création de la session de paiement:', error);
      setIsLoading(false);
    }
  };

  const getButtonText = () => {
    switch (payment.payment_type) {
      case 'deposit':
        return `Payer l'acompte (${payment.amount}€)`;
      case 'final':
        return `Payer le solde (${payment.amount}€)`;
      case 'full':
        return `Payer le montant total (${payment.amount}€)`;
      case 'deposit_guarantee':
        return `Autoriser la caution (${payment.amount}€)`;
      default:
        return `Payer ${payment.amount}€`;
    }
  };

  const getButtonDescription = () => {
    if (payment.payment_type === 'deposit_guarantee') {
      return "Autorisation bancaire de 30 jours - Non encaissée automatiquement";
    }
    return null;
  };

  const isOverdue = new Date(payment.due_date) < new Date() && payment.status === 'pending';
  const isDepositGuarantee = payment.payment_type === 'deposit_guarantee';

  return (
    <div className="space-y-2">
      <Button
        onClick={handlePayment}
        disabled={disabled || isLoading || payment.status === 'paid'}
        variant={isOverdue ? "destructive" : "default"}
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Traitement...
          </>
        ) : (
          <>
            {isDepositGuarantee ? (
              <Shield className="mr-2 h-4 w-4" />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" />
            )}
            {getButtonText()}
          </>
        )}
      </Button>
      
      {getButtonDescription() && (
        <p className="text-xs text-blue-600 text-center">
          ℹ️ {getButtonDescription()}
        </p>
      )}
    </div>
  );
};

export default PaymentButton;
